import styles from './SidePanelFolderInfo.module.css';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { CustomFieldAssociationType } from '@he-novation/config/types/db/enums';
import { FullFolder } from '@he-novation/config/types/folder.types';
import { CustomFieldValuesBody } from '@he-novation/config/types/payloads/custom-field.payload';
import { acl } from '@he-novation/config/utils/acl';
import { Button, ButtonTone } from '@he-novation/design-system/components/buttons/Button/Button';
import { FormField } from '@he-novation/design-system/components/form/FormField/FormField';
import { TagsField } from '@he-novation/design-system/components/form/TagsField/TagsField';
import Accordion from '@he-novation/design-system/components/lists/Accordion/Accordion';
import {
    asyncFolderCustomFieldsFetch,
    asyncFolderCustomFieldsUpdate
} from '@he-novation/front-shared/async/folder.async';
import { getLocalizedDate } from '@he-novation/front-shared/utils/datetime';
import { bytesToSize } from '@he-novation/utils/bytes';
import { getDisplayName } from '@he-novation/utils/user';
import { useAtomValue } from 'jotai';
import { debounce } from 'lodash';

import { useAssociationCustomFields } from '$atoms/custom-fields-atoms';
import { workspaceNameAtom } from '$atoms/workspace-atoms';
import { KeyValueList } from '$components/base/KeyValueList/KeyValueList';
import { CustomFormField } from '$components/CustomFields/CustomFormField';
import { useTranslate } from '$hooks/useTranslate';
import { preferencesSelector } from '$redux/user/userSelectors';

type SidePanelFolderInfoProps = {
    onChange: (diff: {
        tags?: string[];
        labels?: string[];
        description?: string;
        customFields?: CustomFieldValuesBody;
    }) => void;
    folder: FullFolder;
    folderSize: number;
    readonly?: boolean;
};

export function SidePanelFolderInfo({
    folder,
    folderSize,
    onChange,
    readonly
}: SidePanelFolderInfoProps) {
    const { preferences } = useSelector(preferencesSelector);
    const { t, locale } = useTranslate();

    const associatedCustomFields = useAssociationCustomFields(CustomFieldAssociationType.FOLDERS);
    const workspaceName = useAtomValue(workspaceNameAtom);

    const [folderCustomFields, setFolderCustomFields] = useState<CustomFieldValuesBody>({});

    const descriptionRef = useRef<HTMLTextAreaElement>();

    useEffect(() => {
        asyncFolderCustomFieldsFetch(workspaceName, folder.uuid).then(setFolderCustomFields);
    }, [folder.uuid]);

    const updateFolderCustomFields = useMemo(
        () =>
            debounce((customFields: CustomFieldValuesBody) => {
                asyncFolderCustomFieldsUpdate(workspaceName, folder.uuid, customFields);
            }, 1000),
        []
    );

    const infos = useMemo(() => {
        const infos = [
            {
                key: t('folder.Uploaded by {{user}}', { user: '' }),
                value: getDisplayName(folder.creator) || ''
            },
            {
                key: t('common.Creation date'),
                value: getLocalizedDate(new Date(folder.created), locale)
            }
        ];

        if (folder.updated) {
            infos.push({
                key: t('common.Last updated'),
                value: getLocalizedDate(new Date(folder.created), locale)
            });
        }

        infos.push({ key: t('common.Size'), value: bytesToSize(folderSize, locale) });

        return infos;
    }, [folder, folderSize]);

    const { availableTags, availableLabels } = useMemo(() => {
        const availableLabels = new Set([...folder.labels, ...(preferences.labels || [])].sort());
        const availableTags = new Set([...folder.tags, ...(preferences.tags || [])].sort());

        return { availableLabels: [...availableLabels], availableTags: [...availableTags] };
    }, [folder, preferences]);

    const customFieldsFormId = 'folder-custom-fields-form';

    return (
        <>
            <Accordion
                title={t('folder.General information')}
                icon={['file', 'magnifier']}
                isOpen={true}
                className={styles.infoAccordion}
                contentClassName={styles.infos}
            >
                <KeyValueList list={infos} />
            </Accordion>

            <Accordion
                title={t('common.Description')}
                icon={'text_left'}
                isOpen={true}
                className={styles.infoAccordion}
                contentClassName={styles.description}
            >
                <FormField
                    id={'folder-settings-form-description'}
                    type={'textarea'}
                    value={folder.metadata?.notes}
                    name={'description'}
                    componentRef={descriptionRef}
                />
                <Button
                    tone={ButtonTone.Primary}
                    className={styles.descriptionButton}
                    onClick={() => onChange({ description: descriptionRef.current?.value })}
                    disabled={readonly}
                >
                    {t('common.Save')}
                </Button>
            </Accordion>

            {associatedCustomFields.length > 0 && (
                <Accordion
                    title={t('custom-fields.Custom fields')}
                    icon={'form'}
                    isOpen={true}
                    className={styles.infoAccordion}
                >
                    {associatedCustomFields.map((field) => (
                        <CustomFormField
                            key={field.uuid}
                            formId={customFieldsFormId}
                            customField={field}
                            value={folderCustomFields?.[field.name]}
                            readOnly={readonly || !acl.folders.modify(folder.role)}
                            onChange={(value) => {
                                setFolderCustomFields((prev) => {
                                    prev[field.name] = value;
                                    updateFolderCustomFields(prev);
                                    return prev;
                                });
                            }}
                        />
                    ))}
                </Accordion>
            )}

            <Accordion
                title={
                    <>
                        <span>{t('common.Labels')}</span>
                        <span className={styles.counterChips}>{folder.labels.length}</span>
                    </>
                }
                icon={'label'}
                isOpen={true}
                className={styles.infoAccordion}
            >
                <TagsField
                    tags={folder.labels}
                    autocompleteList={availableLabels}
                    setTags={(labels) => onChange({ labels })}
                    toggleOnSpace
                    readOnly={readonly}
                />
            </Accordion>

            <Accordion
                title={
                    <>
                        <span>{t('common.Tags')}</span>
                        <span className={styles.counterChips}>{folder.tags.length}</span>
                    </>
                }
                icon={'label'}
                isOpen={true}
                className={styles.infoAccordion}
            >
                <TagsField
                    tags={folder.tags}
                    autocompleteList={availableTags}
                    setTags={(tags) => onChange({ tags })}
                    toggleOnSpace
                    readOnly={readonly}
                />
            </Accordion>
        </>
    );
}
